import type { Prototype, VersionInfo, Grant } from "./api";

export const VIEWER_BASE = "https://p.ai.rewind.engineering";
const GRANT_URL_BASE = `${VIEWER_BASE}/s/`;

// Presentation only, so it lives portal-side rather than being plumbed from
// lib/config.ts through /config.json. It is not a security limit — nothing
// enforces it — it only decides when a Created cell gets flagged. Keep the
// number in step with config.grantStaleAfterDays if that ever changes.
export const GRANT_STALE_AFTER_DAYS = 90;

// Fixed-width mask, independent of the actual id length — the id is a bearer
// credential and must never appear in the initial render.
export const GRANT_MASKED_LINK = `/s/${"•".repeat(12)}/`;

// The one copy glyph in the portal. The card's main link, the upload-token
// field and each restricted-access-link row all render THIS, so a change to the
// affordance can't land on one of them and miss the others — two copy controls
// on the same card looking like different features is what this replaced.
export const COPY_ICON = "&#128203;";

// Icon-only actions in the grants band. Same 24-viewBox currentColor stroke
// shape as the eye icons so the row's three controls read as one set.
export const TRASH_ICON =
  `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"></path><path d="M9 6V4h6v2"></path><path d="M6 6l1 14h10l1-14"></path><path d="M10 11v6"></path><path d="M14 11v6"></path></svg>`;

const EYE_ICON_HIDDEN =
  `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.6 6.1A9.8 9.8 0 0 1 12 6c6.5 0 10 7 10 7a15.2 15.2 0 0 1-2.1 2.9"></path><path d="M6.6 6.6A15.4 15.4 0 0 0 2 13s3.5 7 10 7a9.7 9.7 0 0 0 4.5-1"></path><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2"></path><path d="M3 3l18 18"></path></svg>`;
export const EYE_ICON_SHOWN =
  `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 13s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"></path><circle cx="12" cy="13" r="3"></circle></svg>`;
export { EYE_ICON_HIDDEN };

export function escape(s: string): string {
  return s.replace(/[&<>"']/g, (c) => (c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : c === '"' ? "&quot;" : "&#39;"));
}

export function relativeTime(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function expiryLabel(p: Prototype): string {
  if (p.kept) return `<span class="expiry-kept">Kept permanently</span>`;
  if (!p.expiresAt) return "";
  const daysLeft = Math.ceil((p.expiresAt * 1000 - Date.now()) / 86400000);
  if (daysLeft <= 0) return `<span class="expiry-soon">Expiring soon</span>`;
  if (daysLeft <= 7) return `<span class="expiry-soon">Expires in ${daysLeft}d</span>`;
  return `<span class="expiry-normal">Expires in ${daysLeft}d</span>`;
}

export function tagChipsHtml(p: Prototype): string {
  if (!p.tags || p.tags.length === 0) return "";
  return `<span class="card-tags">${p.tags.map((t) => `<span class="card-tag">${escape(t)}</span>`).join("")}</span>`;
}

// URL line: friendly URL when set (FRIENDLY tag + mode), else slug URL (LINK tag).
// When `activating`, show the propagating state instead of a live link.
export function urlLineHtml(p: Prototype, activating: boolean): string {
  const slugUrl = `${VIEWER_BASE}/${p.slug}/`;
  if (p.friendlyName) {
    const fUrl = `${VIEWER_BASE}/${p.friendlyName}/`;
    if (activating) {
      return `<div class="proto-urlline">
        <span class="friendlytag">FRIENDLY</span>
        <span class="alias-activating" title="Edge propagation can take up to a minute">&#9203; Activating ${escape(fUrl)}… (live within ~a minute)</span>
      </div>`;
    }
    const mode = p.aliasMode === "pinned" ? `pinned to v${p.pinnedVersion}` : "latest";
    return `<div class="proto-urlline">
      <span class="friendlytag">FRIENDLY</span>
      <a href="${escape(fUrl)}" target="_blank" rel="noopener noreferrer">${escape(fUrl)}</a>
      <span class="alias-mode">${mode}</span>
      <button class="copy-btn" data-url="${escape(fUrl)}" title="Copy friendly URL" aria-label="Copy friendly URL">${COPY_ICON}</button>
    </div>`;
  }
  return `<div class="proto-urlline">
    <span class="slugtag">LINK</span>
    <a href="${escape(slugUrl)}" target="_blank" rel="noopener noreferrer">${escape(slugUrl)}</a>
    <button class="copy-btn" data-url="${escape(slugUrl)}" title="Copy link" aria-label="Copy link">${COPY_ICON}</button>
  </div>`;
}

// Badge colour is an identity signal, not a risk ramp: risk is exposure x
// sensitivity, and a badge only knows exposure, so ranking confidential-but-
// restricted against deliberately-public would mis-rank it. RESTRICTED gets a
// key icon deliberately — never the chain-link glyph used for PUBLIC, which
// it would be too easy to confuse it with at a glance.
function visibilityBadge(p: Prototype): string {
  const v = p.visibility ?? (p.isInternal ? "internal" : "public");
  if (v === "restricted") {
    return `<span class="visibility-badge visibility-badge--restricted">RESTRICTED</span>`;
  }
  return `<span class="visibility-badge ${v === "internal" ? "visibility-badge--internal" : "visibility-badge--public"}">${v === "internal" ? "INTERNAL" : "PUBLIC"}</span>`;
}

export interface CardCtx { isMine: boolean; isAdmin?: boolean; expanded: boolean; activating: boolean; maxRetainedVersions?: number; }

export function renderCard(p: Prototype, ctx: CardCtx): string {
  const slugUrl = `${VIEWER_BASE}/${p.slug}/`;
  const expiry = expiryLabel(p);
  const toggleLabel = ctx.isMine ? "Manage" : "View versions";
  const chevron = ctx.expanded ? "&#9652;" : "&#9662;";
  return `
    <div class="prototype" data-slug="${escape(p.slug)}">
      <div class="proto-line1">
        <a class="proto-title-link" href="${escape(slugUrl)}" target="_blank">${escape(p.title)}</a>
        <span class="version-badge">v${p.currentVersion}</span>
        ${visibilityBadge(p)}
        ${tagChipsHtml(p)}
      </div>
      ${p.description ? `<div class="proto-desc">${escape(p.description)}</div>` : ""}
      ${urlLineHtml(p, ctx.activating)}
      <div class="proto-meta">
        <span>${escape(p.owner)}</span>
        <span>${escape(relativeTime(new Date(p.uploadedAt)))}</span>
        ${expiry ? `<span>${expiry}</span>` : ""}
        <span>${p.versionCount} version${p.versionCount === 1 ? "" : "s"}</span>
        <span class="proto-meta-right">
          ${ctx.isMine ? `<a class="upload-new-link" id="upload-new-${p.slug}" title="Upload a new version">&#8593; Upload new version</a>` : ""}
          <button class="manage-toggle" id="manage-toggle-${p.slug}" data-slug="${p.slug}">${toggleLabel} ${chevron}</button>
        </span>
      </div>
      <div class="proto-manage" id="manage-${p.slug}" style="display:${ctx.expanded ? "" : "none"}">
        ${ctx.isMine ? renderManagePanel(p, ctx) : renderReadonlyManage(p, ctx.isAdmin)}
      </div>
    </div>
  `;
}

export function renderManagePanel(p: Prototype, ctx: CardCtx): string {
  return `
    <div class="manage-cols">
      <div class="manage-left">
        <div class="mgroup">
          <div class="mgroup-label">Friendly URL</div>
          <div id="sharing-${p.slug}">${renderSharing(p, ctx.activating)}</div>
        </div>
        <div class="mgroup">
          <div class="mgroup-label">Versions <button type="button" class="btn-link" id="gen-token-${p.slug}">Generate upload token</button></div>
          <div class="versions-list" id="versions-${p.slug}"><span class="versions-loading">Loading…</span></div>
          ${ctx.maxRetainedVersions ? `<div class="versions-note">Only files for the last ${ctx.maxRetainedVersions} version${ctx.maxRetainedVersions === 1 ? "" : "s"} remain viewable; older versions may appear with Files removed.</div>` : ""}
          <div class="version-upload" id="version-upload-${p.slug}">
            <input type="file" id="version-file-${p.slug}" accept=".zip,application/zip,.html,.htm,text/html" hidden />
            <button class="btn-secondary btn-sm" id="version-choose-${p.slug}">Choose file</button>
            <span class="file-name" id="version-filename-${p.slug}">No file chosen</span>
            <input type="text" id="version-note-${p.slug}" placeholder="Change note (optional)" class="version-note-input" />
            <button class="btn-primary btn-sm" id="version-upload-btn-${p.slug}">Upload</button>
            <span id="version-status-${p.slug}"></span>
          </div>
          <div class="token-section" id="token-section-${p.slug}">
            <div id="token-display-${p.slug}"></div>
          </div>
        </div>
      </div>
      <div class="manage-right">
        <div class="mgroup">
          <div class="mgroup-label">Settings</div>
          ${renderSettingsList(p)}
        </div>
      </div>
    </div>
    ${p.visibility === "restricted" ? `<div class="grants-container" id="grants-${p.slug}"><span class="versions-loading">Loading access links…</span></div>` : ""}
    <div class="manage-danger">
      <button id="del-${p.slug}" class="btn-danger btn-sm">Delete share</button>
      <span class="danger-hint">Permanent. The viewer URL stops working.</span>
    </div>
  `;
}

export function renderSharing(p: Prototype, activating: boolean): string {
  if (activating) {
    return `<span class="alias-activating">&#9203; ${escape(p.friendlyName ?? "")} — activating…</span>`;
  }
  if (p.friendlyName) {
    return `<span class="share-name">${escape(p.friendlyName)}</span>
      <button type="button" class="btn-link" id="alias-rename-${p.slug}">Rename</button>
      <button type="button" class="btn-link" id="alias-latest-${p.slug}">Follow latest</button>
      <button type="button" class="btn-link muted" id="alias-clear-${p.slug}">Remove</button>`;
  }
  return `<input type="text" id="alias-name-${p.slug}" class="alias-name-input" placeholder="friendly-url-name" />
    <button class="btn-secondary btn-sm" id="alias-save-${p.slug}">Set friendly URL</button>`;
}

export function renderSettingsList(p: Prototype): string {
  // Three-valued, matching visibilityBadge() above — not p.isInternal alone,
  // which cannot distinguish internal from restricted and would render a
  // restricted prototype as "Public" here while the card badge above it
  // (correctly) says RESTRICTED.
  const vis = p.visibility ?? (p.isInternal ? "internal" : "public");
  let visRow: string;
  if (vis === "internal") {
    // Internal is a one-way ratchet away from public, but NOT away from
    // restricted: internal -> restricted is the one visibility transition
    // patch.ts supports through this field (public -> restricted is refused as
    // phase 2), and it is the driving case for share links. Without this
    // control the only routes to restricted are a fresh upload or a hand-rolled
    // PATCH, so an existing internal prototype could never be shared out.
    visRow = `<span class="ibadge">INTERNAL</span> <span class="lock">— locked, can't be made public</span> <button type="button" class="btn-link" id="make-restricted-${p.slug}">Make restricted</button>`;
  } else if (vis === "restricted") {
    // Leaving restricted is the transition that can hit the "grants exist"
    // 409 (live access links would otherwise be orphaned) — the button sends
    // `visibility: "internal"`, never `isInternal: true`, because the legacy
    // boolean preserves restricted rather than clearing it (see patch.ts).
    visRow = `<span class="rbadge">RESTRICTED</span> <button type="button" class="btn-link" id="make-internal-${p.slug}">Make internal-only</button>`;
  } else {
    visRow = `Public <button type="button" class="btn-link" id="make-internal-${p.slug}">Make internal-only</button>`;
  }
  const retRow = p.kept
    ? `Kept permanently <button type="button" class="btn-link" id="unkeep-${p.slug}">Let it expire</button>`
    : `${p.expiresAt ? `Expires in ${Math.max(0, Math.ceil((p.expiresAt * 1000 - Date.now()) / 86400000))} days` : "No expiry"} <button type="button" class="btn-link" id="keep-${p.slug}">Keep permanently</button>`;
  const tagsVal = p.tags && p.tags.length ? p.tags.map((t) => `<span class="card-tag">${escape(t)}</span>`).join(" ") : `<span class="muted">none</span>`;
  return `
    <div class="prow"><span class="pkey">Title</span><span class="pval">${escape(p.title)}</span><button type="button" class="btn-link" id="edit-meta-${p.slug}">Edit</button></div>
    <div class="prow"><span class="pkey">Description</span><span class="pval ${p.description ? "" : "muted"}">${p.description ? escape(p.description) : "none"}</span><button type="button" class="btn-link" id="edit-meta-desc-${p.slug}">Edit</button></div>
    <div class="prow"><span class="pkey">Tags</span><span class="pval">${tagsVal}</span><button type="button" class="btn-link" id="edit-tags-${p.slug}">Edit</button></div>
    <div class="prow"><span class="pkey">Visibility</span><span class="pval">${visRow}</span></div>
    <div class="prow"><span class="pkey">Retention</span><span class="pval">${retRow}</span></div>
    <div class="prow"><span class="pkey">Ownership</span><span class="pval">${escape(p.owner)} <span class="muted">(you)</span> <button type="button" class="btn-link" id="transfer-${p.slug}">Transfer&hellip;</button></span></div>
  `;
}

function ageLabel(createdAt: string): string {
  const days = Math.floor((Date.now() - new Date(createdAt).getTime()) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? "1 month ago" : `${months} months ago`;
}

// The band exists ONLY for restricted prototypes — not collapsed, not empty:
// absent. Restricted is expected to be a small minority, which is what makes
// a whole new region of the panel affordable. There is no usage telemetry —
// no lastUsedAt, no open count — by design; Created plus a staleness flag is
// the substitute for "is this link still needed", not "was it used".
export function renderGrantsBand(p: Prototype, grants: Grant[], staleAfterDays: number): string {
  if (p.visibility !== "restricted") return "";

  // Row affordances deliberately mirror the card's main link row — masked value,
  // then the SAME .copy-btn — with one deliberate difference kept: the eye
  // toggle. The share URL is a bearer credential (anyone holding it gets in),
  // so it stays masked until asked for; the slug link isn't one (sign-in still
  // applies) and is shown in full. Same look, credential still masked.
  //
  // The copy button carries no data-url on purpose: the URL lives in exactly one
  // place in the DOM (.linkbox's data-full, which the eye toggle owns), and the
  // generic .copy-btn[data-url] wiring in ui.ts therefore leaves these rows to
  // wireGrants instead of double-binding them.
  const rows = grants.map((g) => {
    const stale = Date.now() - new Date(g.createdAt).getTime() > staleAfterDays * 86400000;
    const created = stale
      ? `<span class="gstale" title="Created over ${staleAfterDays} days ago">${ageLabel(g.createdAt)}</span>`
      : `<span class="gfresh">${ageLabel(g.createdAt)}</span>`;
    return `
      <tr data-grant="${escape(g.id)}">
        <td><div class="glabel">${escape(g.label)}</div><div class="gsub">by ${escape(g.createdBy)}</div></td>
        <td class="gcell">
          <span class="linkbox" data-full="${escape(GRANT_URL_BASE + g.id + "/")}">
            <span class="linkval">${GRANT_MASKED_LINK}</span>
            <button type="button" class="iconbtn grant-eye" title="Show link" aria-label="Show link">${EYE_ICON_HIDDEN}</button>
          </span>
          <button type="button" class="copy-btn grant-copy" title="Copy link" aria-label="Copy link">${COPY_ICON}</button>
        </td>
        <td>${created}</td>
        <td class="gacts">
          <button type="button" class="iconbtn iconbtn--danger grant-revoke" title="Revoke link" aria-label="Revoke link">${TRASH_ICON}</button>
        </td>
      </tr>`;
  }).join("");

  const body = grants.length
    ? `<table class="gtable">
         <thead><tr>
           <th style="width:25%">Shared with</th><th style="width:46%">Link</th>
           <th style="width:17%">Created</th><th style="width:12%"></th>
         </tr></thead>
         <tbody>${rows}</tbody>
       </table>`
    : `<div class="bandempty"><b>No access links yet.</b> Right now only Rewinders can open this &mdash; the same as Internal. Create a link to let someone outside Rewind in.</div>`;

  return `
    <div class="band">
      <div class="bandhead">
        <span class="bandtitle">Restricted access links</span>
        ${grants.length ? `<span class="bandcount">${grants.length} active</span>` : ""}
        <span class="bandspace"></span>
        <button type="button" class="btn-secondary btn-sm" id="grant-new-${p.slug}">+ New share link</button>
      </div>
      ${body}
    </div>`;
}

// The admin affordance. Deliberately offers ONLY transfer: delete, keep,
// unkeep and generate-token are owner-only with no admin bypass, so any of
// them here would be a control that 403s. Transfer is what turns that around —
// it hands the prototype to someone who can use them.
export function renderAdminStrip(p: Prototype): string {
  return `
    <div class="admin-strip">
      <span class="admin-chip">ADMIN</span>
      <span>You do not own this share. You can hand it over, and manage its access links.</span>
      <span class="bandspace"></span>
      <button type="button" class="btn-secondary btn-sm" id="transfer-${p.slug}">Transfer&hellip;</button>
    </div>`;
}

export function renderReadonlyManage(p: Prototype, isAdmin = false): string {
  // Admins can already list, create and revoke grants (grants.ts admits them),
  // so the band is legitimate here — but only for a restricted prototype, the
  // same condition renderManagePanel applies.
  const grants = isAdmin && p.visibility === "restricted"
    ? `<div class="grants-container" id="grants-${p.slug}"><span class="versions-loading">Loading access links&hellip;</span></div>`
    : "";
  return `
    ${isAdmin ? renderAdminStrip(p) : ""}
    <div class="mgroup">
      <div class="mgroup-label">Versions</div>
      <div class="versions-list" id="versions-${p.slug}"><span class="versions-loading">Loading&hellip;</span></div>
    </div>
    ${grants}
  `;
}

export function renderVersionRow(p: Prototype, v: VersionInfo, isMine: boolean): string {
  const isCurrent = v.version === p.currentVersion;
  const isDeleted = v.status === "deleted";
  const isFailed = v.status === "failed";
  const isPending = v.status === "pending";
  const viewUrl = `${VIEWER_BASE}/${p.slug}/v${v.version}/`;
  let action: string;
  if (isDeleted) action = `<span class="version-deleted-label">Deleted</span>`;
  else if (isFailed) action = `<span class="version-failed-label" title="${escape(v.error ?? "Extraction failed")}">Failed</span>`;
  else if (isPending) action = `<span class="version-pending-label">Pending</span>`;
  else if (!v.filesRetained) action = `<span class="version-pruned">Files removed</span>`;
  else action = `<a href="${viewUrl}" target="_blank" rel="noopener noreferrer">View</a>`;
  const canDelete = isMine && !isCurrent && !isDeleted;
  const canPin = isMine && p.friendlyName && v.status === "ready" && v.filesRetained;
  const pinTxt = p.aliasMode === "pinned" && p.pinnedVersion === v.version ? "Pinned" : "Pin";
  const acts = [
    action,
    canPin ? `<button type="button" class="btn-link pin-version" data-slug="${p.slug}" data-version="${v.version}">${pinTxt}</button>` : "",
    canDelete ? `<button type="button" class="btn-link version-del-btn" data-slug="${p.slug}" data-version="${v.version}">Delete</button>` : "",
  ].filter(Boolean).join(" · ");
  const meta = isDeleted ? "" : `${escape(v.uploadedBy)} · ${relativeTime(new Date(v.uploadedAt))}`;
  return `
    <div class="vrow">
      <span class="vbadgecell">${isCurrent ? `<span class="vcur">CURRENT</span>` : ""}</span>
      <span class="vnum">v${v.version}</span>
      <span class="vmeta">${meta}</span>
      <span class="vacts">${acts}</span>
    </div>
  `;
}
